import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onAnchorClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onAnchorClick);
  }

  /**
   * Handle anchor links inside the menu drawer (such as promo banners or page jump links).
   * Closes the drawer and smoothly scrolls to the target section with sticky header offset.
   * @param {MouseEvent} event
   */
  #onAnchorClick = (event) => {
    const link = event.target.closest('a');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href) return;

    if (href.includes('#')) {
      const hashIndex = href.indexOf('#');
      const hash = href.slice(hashIndex);
      if (hash && hash !== '#' && hash !== '#!') {
        const path = href.slice(0, hashIndex);
        const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const isHomePage = window.location.pathname === '/' || window.location.pathname === rootPath;
        const isTargetingHome = !path || path === '/' || path === rootPath;

        // When clicked from product page (or any non-homepage), navigate to homepage with hash
        if (isTargetingHome && !isHomePage) {
          event.preventDefault();
          this.close();
          const destination = (rootPath.endsWith('/') ? rootPath.slice(0, -1) : rootPath) + '/' + hash;
          setTimeout(() => {
            window.location.href = destination;
          }, 80);
          return;
        }

        const isCurrentPage = (!path && isHomePage) || (path && (path === window.location.pathname || path === window.location.href));

        if (isCurrentPage || isHomePage) {
          const targetId = hash.replace('#', '');
          const targetEl = document.getElementById(targetId) || document.querySelector(`[id="${targetId}"]`) || document.querySelector(hash);

          if (targetEl) {
            event.preventDefault();

            // 1. Close drawer cleanly and immediately release scroll lock
            this.close();
            document.documentElement.removeAttribute('scroll-lock');

            // 2. Smoothly scroll to target section with sticky header offset
            setTimeout(() => {
              const header = document.querySelector('header-component') || document.querySelector('.header-wrapper') || document.querySelector('header');
              const headerHeight = header ? header.offsetHeight : 80;
              const targetTop = targetEl.getBoundingClientRect().top + window.pageYOffset - headerHeight - 15;

              window.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth'
              });

              if (window.history && window.history.pushState) {
                window.history.pushState(null, '', hash);
              }
            }, 100);
            return;
          }
        }
      }
    }

    // Category cards and drawer navigation links: close drawer and ensure navigation
    if (link.classList.contains('menu-drawer-shop-by__card') || link.closest('.menu-drawer-shop-by')) {
      const targetUrl = link.getAttribute('href') || link.href;
      if (targetUrl && targetUrl !== '#' && !targetUrl.startsWith('javascript:')) {
        this.close();
        if (link.target !== '_blank') {
          setTimeout(() => {
            if (window.location.pathname !== targetUrl && window.location.href !== targetUrl) {
              window.location.href = targetUrl;
            }
          }, 50);
        }
      }
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
